import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { typeMeta, PRIORITIES, prioMeta } from "@/lib/meta";
import { CalendarDays, ChevronLeft, ChevronRight, Layers } from "lucide-react";
import { cn } from "@/lib/utils";

// Simple week-based roadmap: shows epics as bars using start_date + target_date.
// If dates missing, epic gets a placeholder track prompting to schedule.
export default function Roadmap({ projectId: propProjectId }) {
  const { projects, openIssue } = useApp();
  const [projectId, setProjectId] = useState(propProjectId || "all");
  const [epics, setEpics] = useState([]);
  const [zoom, setZoom] = useState("weeks"); // days|weeks|months
  const [anchor, setAnchor] = useState(() => new Date());

  useEffect(() => {
    const params = { issue_type: "epic" };
    if (projectId !== "all") params.project_id = projectId;
    api.get("/issues", { params }).then(r => setEpics(r.data));
  }, [projectId]);

  // Build timeline window
  const cellCount = zoom === "days" ? 30 : zoom === "months" ? 12 : 12; // 12 weeks or 12 months
  const cellMs = zoom === "days" ? 86400000 : zoom === "months" ? 2592000000 : 604800000;
  const start = useMemo(() => {
    const d = new Date(anchor);
    if (zoom === "weeks") { const day = d.getDay(); d.setDate(d.getDate() - day - 14); d.setHours(0,0,0,0); }
    else if (zoom === "months") { d.setDate(1); d.setMonth(d.getMonth() - 2); d.setHours(0,0,0,0); }
    else { d.setDate(d.getDate() - 5); d.setHours(0,0,0,0); }
    return d;
  }, [anchor, zoom]);
  const end = new Date(start.getTime() + cellCount * cellMs);

  const cells = Array.from({ length: cellCount }, (_, i) => {
    const s = new Date(start.getTime() + i * cellMs);
    const label = zoom === "months"
      ? s.toLocaleDateString(undefined, { month: "short", year: "2-digit" })
      : zoom === "weeks"
        ? `W${String(getWeek(s)).padStart(2,"0")}`
        : s.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return { s, label };
  });

  const setDates = async (epic, patch) => {
    const r = await api.patch(`/issues/${epic.id}`, patch);
    setEpics(es => es.map(e => e.id === epic.id ? r.data : e));
  };

  const scheduleFallback = (epic, i) => {
    // If no dates, place a suggested 4-cell bar starting at index i
    const s = new Date(start.getTime() + i * cellMs);
    const t = new Date(s.getTime() + 4 * cellMs);
    setDates(epic, { start_date: s.toISOString(), target_date: t.toISOString() });
  };

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Planning</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Roadmap</h1>
        </div>
        <div className="flex items-center gap-2">
          {!propProjectId && (
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="w-56 h-9" data-testid="roadmap-project"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={zoom} onValueChange={setZoom}>
            <SelectTrigger className="w-32 h-9" data-testid="roadmap-zoom"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="days">Days</SelectItem>
              <SelectItem value="weeks">Weeks</SelectItem>
              <SelectItem value="months">Months</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex border border-border rounded-md overflow-hidden">
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-none" onClick={()=>setAnchor(a => new Date(a.getTime() - cellMs * 4))} data-testid="roadmap-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" className="h-9 rounded-none px-3" onClick={()=>setAnchor(new Date())}>Today</Button>
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-none" onClick={()=>setAnchor(a => new Date(a.getTime() + cellMs * 4))} data-testid="roadmap-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
      {epics.length === 0 ? (
        <Card className="p-16 text-center">
          <Layers className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-display text-lg font-semibold">No epics yet</div>
          <div className="text-sm text-muted-foreground mt-1">Create an Epic issue to see it on the roadmap.</div>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid border-b border-border sticky top-0 bg-card z-10" style={{gridTemplateColumns: `260px repeat(${cellCount}, minmax(70px, 1fr))`}}>
              <div className="px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground font-medium">Epic</div>
              {cells.map((c,i)=>(
                <div key={i} className="px-2 py-2 text-[10px] uppercase tracking-widest text-muted-foreground text-center border-l border-border/60 font-medium">{c.label}</div>
              ))}
            </div>
            {epics.map(epic => {
              const T = typeMeta(epic.issue_type); const P = prioMeta(epic.priority);
              const es = epic.start_date ? new Date(epic.start_date) : null;
              const ee = epic.target_date ? new Date(epic.target_date) : null;
              let barStart = 0, barSpan = 0;
              if (es && ee && ee > start && es < end) {
                barStart = Math.max(0, Math.floor((es - start) / cellMs));
                barSpan = Math.min(cellCount - barStart, Math.ceil((ee - Math.max(es, start)) / cellMs));
              }
              return (
                <div key={epic.id} className="grid border-b border-border/60 hover:bg-accent/30 transition-colors" style={{gridTemplateColumns: `260px repeat(${cellCount}, minmax(70px, 1fr))`}} data-testid={`roadmap-row-${epic.key}`}>
                  <button className="px-3 py-3 text-left flex items-center gap-2 border-r border-border" onClick={()=>openIssue(epic.id)}>
                    <T.icon className={cn("w-4 h-4 shrink-0", T.cls)} />
                    <span className="font-mono text-[10px] text-muted-foreground shrink-0">{epic.key}</span>
                    <span className="text-sm truncate">{epic.summary}</span>
                  </button>
                  <div className="col-span-full grid relative" style={{gridColumn: `2 / span ${cellCount}`, gridTemplateColumns: `repeat(${cellCount}, minmax(70px, 1fr))`}}>
                    {cells.map((_,i)=>(
                      <div key={i} className="border-l border-border/40" />
                    ))}
                    {barSpan > 0 && (
                      <button
                        onClick={()=>openIssue(epic.id)}
                        data-testid={`roadmap-bar-${epic.key}`}
                        className="absolute inset-y-2 rounded-md bg-primary/20 border border-primary hover:bg-primary/30 transition-colors flex items-center gap-2 px-2 text-xs font-medium overflow-hidden"
                        style={{left: `${(barStart / cellCount) * 100}%`, width: `${(barSpan / cellCount) * 100}%`}}>
                        <P.icon className={cn("w-3.5 h-3.5 shrink-0", P.cls)} />
                        <span className="truncate">{epic.summary}</span>
                      </button>
                    )}
                    {barSpan === 0 && (
                      <div className="absolute inset-y-2 flex items-center gap-2 pl-2 text-[10px] text-muted-foreground" style={{left: `0%`}}>
                        <Button size="sm" variant="ghost" className="h-6 text-[11px] gap-1" onClick={()=>scheduleFallback(epic, Math.floor(cellCount / 3))} data-testid={`roadmap-schedule-${epic.key}`}>
                          <CalendarDays className="w-3 h-3" /> Schedule
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

function getWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const start = new Date(Date.UTC(date.getUTCFullYear(),0,1));
  return Math.ceil(((date - start) / 86400000 + 1) / 7);
}
